import { Label } from '@/ui/base/label';
import { RadioGroup, RadioGroupItem } from '@/ui/base/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';

import type { OpcoesDoModulo, PerfilOferecido, Tenant } from './contrato';

const TITULO_DO_GRUPO: Record<Tenant, string> = {
  TEG: 'Equipe da DTP e da SME',
  DRE: 'Diretoria regional de educação',
  CREDENCIADO: 'Empresa credenciada',
};

interface Props {
  opcoes: OpcoesDoModulo;
  /** Só estes perfis aparecem (a edição limita pela família da conta). */
  permitidos?: readonly string[];
  perfil: string;
  recorteId: string;
  aoMudarPerfil: (perfil: string) => void;
  aoMudarRecorte: (recorteId: string) => void;
  erroDoPerfil?: string;
  erroDoRecorte?: string;
  /**
   * Na edição, a empresa da conta não muda (ela nasce do credenciamento ou do
   * convite): o campo vira texto com o nome dela.
   */
  empresaFixa?: string | null;
}

/**
 * Perfil de acesso com a explicação de cada um, agrupado por quem é a pessoa,
 * e o recorte quando o perfil pede (diretoria regional ou empresa). Usado no
 * convite e na edição. Perfil que quem está logado não pode dar não aparece
 * (regra 7 de interface; a API confere de novo).
 */
export function CampoDePerfil({
  opcoes,
  permitidos,
  perfil,
  recorteId,
  aoMudarPerfil,
  aoMudarRecorte,
  erroDoPerfil,
  erroDoRecorte,
  empresaFixa,
}: Props) {
  const visiveis = opcoes.perfis.filter(
    (p) => p.podeConceder && (!permitidos || permitidos.includes(p.codigo)),
  );
  const grupos = (['TEG', 'DRE', 'CREDENCIADO'] as Tenant[])
    .map((tenant) => ({ tenant, perfis: visiveis.filter((p) => p.tenant === tenant) }))
    .filter((g) => g.perfis.length > 0);
  const escolhido: PerfilOferecido | undefined = opcoes.perfis.find((p) => p.codigo === perfil);
  const recortes =
    escolhido?.tenant === 'DRE'
      ? opcoes.dres
      : escolhido?.tenant === 'CREDENCIADO'
        ? opcoes.credenciados
        : [];

  return (
    <div className="space-y-5">
      <fieldset className="space-y-3" aria-describedby={erroDoPerfil ? 'perfil-erro' : undefined}>
        <legend className="text-sm font-medium">Perfil de acesso</legend>
        <RadioGroup value={perfil} onValueChange={aoMudarPerfil} className="gap-4">
          {grupos.map((grupo) => (
            <div key={grupo.tenant} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {TITULO_DO_GRUPO[grupo.tenant]}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {grupo.perfis.map((p) => (
                  <Label
                    key={p.codigo}
                    htmlFor={`perfil-${p.codigo}`}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 font-normal has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                  >
                    <RadioGroupItem id={`perfil-${p.codigo}`} value={p.codigo} className="mt-0.5" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">{p.rotulo}</span>
                      <span className="block text-xs text-muted-foreground">{p.descricao}</span>
                    </span>
                  </Label>
                ))}
              </div>
            </div>
          ))}
        </RadioGroup>
        {erroDoPerfil && (
          <p id="perfil-erro" className="text-sm text-destructive-text">
            {erroDoPerfil}
          </p>
        )}
      </fieldset>

      {escolhido && escolhido.tenant === 'CREDENCIADO' && empresaFixa !== undefined && (
        <div className="space-y-1">
          <p className="text-sm font-medium">Empresa credenciada</p>
          <p className="text-sm text-foreground">{empresaFixa ?? 'Sem empresa vinculada'}</p>
          <p className="text-xs text-muted-foreground">A empresa da conta não muda por aqui.</p>
        </div>
      )}

      {escolhido &&
        escolhido.tenant !== 'TEG' &&
        !(escolhido.tenant === 'CREDENCIADO' && empresaFixa !== undefined) && (
          <div className="space-y-2">
            <Label htmlFor="campo-recorte">
              {escolhido.tenant === 'DRE' ? 'Diretoria regional' : 'Empresa credenciada'}
            </Label>
            <Select value={recorteId} onValueChange={aoMudarRecorte}>
              <SelectTrigger
                id="campo-recorte"
                aria-invalid={Boolean(erroDoRecorte)}
                aria-describedby={erroDoRecorte ? 'recorte-erro' : undefined}
              >
                <SelectValue placeholder="Escolha" />
              </SelectTrigger>
              <SelectContent>
                {recortes.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {escolhido.tenant === 'DRE'
                ? 'A pessoa só vê os dados desta diretoria.'
                : 'A pessoa só vê os dados desta empresa.'}
            </p>
            {erroDoRecorte && (
              <p id="recorte-erro" className="text-sm text-destructive-text">
                {erroDoRecorte}
              </p>
            )}
          </div>
        )}
    </div>
  );
}
